import React from 'react'
import { styled } from '@mui/material/styles'
import PropTypes from 'prop-types'
import ThemeConfig from '../../theme/ThemeConfig'
import {
  Select as MuiSelect,
  FormControl,
  MenuItem,
  InputLabel,
  FormHelperText,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import Checkbox from '../Checkbox/Checkbox'

const PREFIX = 'Select'

const classes = {
  root: `${PREFIX}-root`,
  bulkActionRoots: `${PREFIX}-bulkActionRoots`,
  list: `${PREFIX}-list`,
  selectIcon: `${PREFIX}-selectIcon`,
  paper: `${PREFIX}-paper`,
  cssHelperText: `${PREFIX}-cssHelperText`,
}

const StyledFormControl = styled(FormControl)(() => {
  return {
    [`&.${classes.root}`]: {
      '& .MuiSelect-select': {
        '&:focus': {
          backgroundColor: 'transparent',
        },
      },
      '& .MuiSelect-iconOutlined': {
        right: '0px',
      },
      '& .MuiInputBase-root': {
        height: '40px',
        '&.Mui-disabled': {
          backgroundColor: ThemeConfig.palette.inputDisabled.backgroundColor,
        },
      },
      '& .MuiOutlinedInput-input': {
        '&.Mui-disabled': {
          color: ThemeConfig.palette.secondary.dark,
        },
        color: ThemeConfig.palette.secondary.darkest,
        fontSize: ThemeConfig.typography.body1.fontSize,
      },
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          color: ThemeConfig.palette.secondary.darkest,
          borderColor: ThemeConfig.palette.secondary.darkest,
        },
        '&.Mui-focused fieldset': {
          borderColor: ThemeConfig.palette.secondary.darkest,
          color: ThemeConfig.palette.secondary.darkest,
        },
        '&.Mui-disabled fieldset': {
          borderColor: ThemeConfig.palette.secondary.main,
          color: ThemeConfig.palette.secondary.main,
        },
        '&.Mui-error': {
          '& fieldset': {
            borderColor: ThemeConfig.palette.error.main,
          },
          '&.Mui-focused fieldset': {
            borderColor: ThemeConfig.palette.error.main,
          },
        },
      },
      '&.MuiInputLabel-outlined': {
        '&.MuiInputLabel-shrink': {
          fontSize: ThemeConfig.typography.inputText.fontSize,
          transform: 'translate(14px, -6px) scale(0.75)',
        },
        '&.Mui-disabled ': {
          color: ThemeConfig.palette.secondary.dark,
        },
        fontSize: ThemeConfig.typography.inputText.fontSize,
        color: ThemeConfig.palette.secondary.main,
        transform: 'translate(14px, 12px) scale(1)',
      },
      '& label.Mui-error': {
        color: ThemeConfig.palette.error.main,
      },
      '&.MuiFormHelperText-root.Mui-error': {
        color: ThemeConfig.palette.error.main,
      },
    },
    [`&.${classes.bulkActionRoots}`]: {
      '&.MuiSelect-select': {
        '&:focus': {
          backgroundColor: 'transparent',
        },
      },
      '& .MuiSelect-iconOutlined': {
        right: '0px',
      },
      '& .MuiInputBase-root': {
        height: '40px',
        margin: 'auto',
        width: '100%',
        color: ThemeConfig.palette.background.paper,
        '&.Mui-disabled': {
          opacity: '0.6',
        },
      },
      '&.MuiOutlinedInput-input': {
        '&.Mui-disabled': {
          color: ThemeConfig.palette.background.paper,
        },
        color: ThemeConfig.palette.background.paper,
        fontSize: ThemeConfig.typography.body1.fontSize,
      },
      '&.MuiOutlinedInput-root': {
        '& fieldset': {
          color: ThemeConfig.palette.background.paper,
          borderColor: ThemeConfig.palette.background.paper,
        },
        '&.Mui-focused fieldset': {
          borderColor: ThemeConfig.palette.background.paper,
          color: ThemeConfig.palette.background.paper,
        },
        '&.Mui-disabled fieldset': {
          borderColor: ThemeConfig.palette.background.paper,
          color: ThemeConfig.palette.background.paper,
          opacity: '0.6',
        },
        '&.Mui-error': {
          '& fieldset': {
            borderColor: ThemeConfig.palette.error.main,
          },
          '&.Mui-focused fieldset': {
            borderColor: ThemeConfig.palette.error.main,
          },
        },
      },
      '&.MuiInputLabel-outlined': {
        margin: 'auto',
        // marginTop: '8px',
        fontSize: ThemeConfig.typography.body1.fontSize,
        color: ThemeConfig.palette.background.paper,
        transform: 'translate(14px, 12px) scale(1)',
        '&.MuiInputLabel-shrink': {
          fontSize: ThemeConfig.typography.body1.fontSize,
          transform: 'translate(14px, -6px) scale(0.75)',
        },
        '&.Mui-disabled ': {
          color: ThemeConfig.palette.background.paper,
          opacity: '0.6',
        },
      },
      '& label.Mui-error': {
        color: ThemeConfig.palette.error.main,
      },
      '& .MuiFormHelperText-root.Mui-error': {
        color: ThemeConfig.palette.error.main,
      },
    },
    [`& .${classes.list}`]: {
      '& .MuiListItem-root': {
        '&:hover': {
          backgroundColor: ThemeConfig.palette.secondary.light,
        },
        '&.Mui-disabled': {
          color: ThemeConfig.palette.secondary.main,
        },
        '&.Mui-selected': {
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: ThemeConfig.palette.secondary.light,
          },
        },
      },

      '&.MuiListItem-divider': {
        borderBottomColor: ThemeConfig.palette.secondary.main,
      },
    },
    [`& .${classes.selectIcon}`]: {
      color: ThemeConfig.palette.background.paper,
    },
    [`& .${classes.paper}`]: {
      '&.MuiMenu-paper': {
        marginTop: '-12px',
      },
    },
    [`& .${classes.cssHelperText}`]: {
      fontStyle: 'italic',
      color: ThemeConfig.palette.secondary.main,
    },
  }
})

const Select = props => {
  const {
    options,
    label,
    hintText,
    disabled,
    optionValueKey,
    optionLabelKey,
    error,
    required,
    onChange,
    errorText,
    selectedValue,
    fullWidth,
    multiple,
    widthRange,
    bulkActions,
    clearContent,
    ...others
  } = props

  const [currentValue, setCurrentValue] = React.useState(() => {
    return (selectedValue ?? multiple) ? [] : ''
  })

  const inputLabel = React.useRef(null)
  const [labelWidth, setLabelWidth] = React.useState(0)
  React.useEffect(() => {
    setLabelWidth(inputLabel.current.offsetWidth)
  }, [])

  const helperText = error && errorText ? errorText : hintText

  const handleChange = event => {
    setCurrentValue(event.target.value)
    onChange(event)
  }

  const rootClasses = bulkActions ? classes.bulkActionRoots : classes.root
  const paperClass = bulkActions ? '' : classes.paper
  const selectIconClass = bulkActions ? classes.selectIcon : ''

  React.useEffect(() => {
    if (selectedValue !== undefined && selectedValue !== currentValue) {
      setCurrentValue(selectedValue)
    }
  }, [selectedValue, currentValue])

  return (
    <StyledFormControl
      variant={'outlined'}
      disabled={disabled}
      classes={{ root: rootClasses }}
      error={error}
      required={required}
      fullWidth={fullWidth}
      style={widthRange ? widthRange : {}}
    >
      <InputLabel ref={inputLabel}>{label}</InputLabel>
      <MuiSelect
        multiple={multiple}
        variant={'outlined'}
        label={label}
        onChange={handleChange}
        value={currentValue}
        inputProps={{ name: optionValueKey }}
        MenuProps={{
          marginThreshold: 8,
          getContentAnchorEl: null,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          classes: { list: classes.list, paper: paperClass },
        }}
        classes={{ icon: selectIconClass }}
        renderValue={value =>
          multiple
            ? value.map(val => val[optionLabelKey]).join(', ')
            : value[optionLabelKey]
        }
        {...others}
      >
        {options.map(option => (
          <MenuItem
            key={option[optionValueKey]}
            value={option}
            disabled={option.disabled}
            divider={option.divider}
            dense
          >
            {multiple && (
              <Checkbox
                disabled={option.disabled}
                checked={
                  currentValue.findIndex(val => {
                    return val[optionValueKey] === option[optionValueKey]
                  }) > -1
                }
              />
            )}
            {!multiple && (
              <CheckIcon
                style={{
                  marginRight: '3px',
                  visibility:
                    currentValue[optionValueKey] === option[optionValueKey]
                      ? 'visible'
                      : 'hidden',
                }}
                htmlColor={ThemeConfig.palette.secondary.dark}
              />
            )}
            {option[optionLabelKey]}
          </MenuItem>
        ))}
      </MuiSelect>
      {helperText && (
        <FormHelperText classes={{ root: classes.cssHelperText }}>
          {helperText}
        </FormHelperText>
      )}
    </StyledFormControl>
  )
}

Select.propTypes = {
  options: PropTypes.array.isRequired,
  hintText: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  optionValueKey: PropTypes.string.isRequired,
  optionLabelKey: PropTypes.string.isRequired,
  error: PropTypes.bool,
  errorText: PropTypes.string,
  required: PropTypes.bool,
  onChange: PropTypes.func,
  selectedValue: PropTypes.object,
  fullWidth: PropTypes.bool,
  multiple: PropTypes.bool,
  widthRange: PropTypes.object,
  bulkActions: PropTypes.bool,
}

Select.muiName = MuiSelect.muiName
export default Select
